// The lift: the cut between rooms (M11). Brass doors close over the room you leave, the floor dial swings to the
// destination floor, the lamp lights with a ding, and the doors part on the new room with its name on a brass plate
// across the seam, split as they open. A move on one floor keeps the lift but is shorter: the dial stays, the plate and
// the lamp carry it. Under reduced motion it is one still (doors shut, lamp lit) held for a moment, then the room.
// Drawn at art resolution (a whole number of device pixels per art pixel) in the world's palette and pixel faces,
// over the world canvas, in screen space. Pure drawing and timing: the stage owns the clock.
import type { FloorId } from './assets';
import { LEVEL_OF_FLOOR, LEVEL_WORD, type Level } from './plan';
import type { Viewport } from './camera';

export interface LiftTrip {
  /** The destination's name for the plate. */
  name: string;
  from: Level;
  to: Level;
  reduced: boolean;
  /** Where the needle starts on the dial (0 = G .. 4), when a trip is redirected mid-swing; else at `from`. */
  fromPos?: number;
}

type Span = [number, number];
export interface LiftTimeline {
  total: number;
  /** When the camera cuts to the new room (the doors are shut). */
  swap: number;
  close: Span;
  open: Span;
  lintelIn: Span;
  lintelOut: Span;
  /** The needle's swing; null on one floor. */
  travel: Span | null;
  ding: number;
}

const DIAL: Level[] = ['G', '1', '2', '3', '4'];

const STILL: LiftTimeline = { total: 600, swap: 0, close: [0, 0], open: [600, 600], lintelIn: [0, 0], lintelOut: [600, 600], travel: null, ding: 0 };
const ONE_FLOOR: LiftTimeline = { total: 460, swap: 200, close: [40, 190], open: [270, 440], lintelIn: [40, 120], lintelOut: [380, 460], travel: null, ding: 220 };
const FLOORS: LiftTimeline = { total: 780, swap: 250, close: [50, 240], open: [570, 760], lintelIn: [50, 150], lintelOut: [690, 780], travel: [260, 520], ding: 530 };

/** The prototype Thomas picked ran about 1.3 s between floors. FEEDBACK_1 (2026-10-09, "loading time should be
 * shorter") halved it with the same beats in the same order: about 0.78 s between floors, 0.46 s on one floor, and
 * the room shows as soon as the doors part (WorldStage). Reduced motion stays a still. */
export function liftTimeline(trip: LiftTrip): LiftTimeline {
  if (trip.reduced) return STILL;
  return trip.from === trip.to && trip.fromPos === undefined ? ONE_FLOOR : FLOORS;
}

const DIAL_POS = (l: Level) => DIAL.indexOf(l);

/** Where the needle points at `ms` (0 = G .. 4). */
export function needleAt(trip: LiftTrip, ms: number): number {
  const to = DIAL_POS(trip.to);
  const tl = liftTimeline(trip);
  if (trip.reduced || !tl.travel) return to;
  const from = trip.fromPos ?? DIAL_POS(trip.from);
  return from + (to - from) * ease(seg(ms, tl.travel));
}

export function tripBetween(fromFloor: FloorId, toFloor: FloorId, name: string, reduced: boolean): LiftTrip {
  return { name, from: LEVEL_OF_FLOOR[fromFloor] ?? '1', to: LEVEL_OF_FLOOR[toFloor] ?? '1', reduced };
}

const clamp = (t: number) => Math.max(0, Math.min(1, t));
const seg = (ms: number, [a, b]: Span) => (b <= a ? (ms >= b ? 1 : 0) : clamp((ms - a) / (b - a)));
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
/** Pixel-honest motion: whole steps, not a smooth glide. */
const steps = (t: number, n: number) => Math.floor(t * n) / n;

/** How far the doors stand open at `ms`: 1 open, 0 shut. */
export function doorsOpen(tl: LiftTimeline, ms: number): number {
  if (ms < tl.swap) return 1 - steps(ease(seg(ms, tl.close)), 10);
  return steps(ease(seg(ms, tl.open)), 10);
}

/** Where to start a new trip so its doors stand as open as the old trip's do now (a second move during a lift). */
export function resumeAt(tl: LiftTimeline, open: number): number {
  if (open >= 1) return 0;
  for (let ms = tl.close[0]; ms <= tl.close[1]; ms += 5) if (doorsOpen(tl, ms) <= open) return ms;
  return tl.close[1];
}

// ---------------------------------------------------------------- drawing

const PAL: Record<string, string> = {
  ol: '#160f1a', r1: '#2a2634', p1: '#e7dbbf', lw: '#fff4c4',
  h0: '#2c130c', h1: '#4b2015', h2: '#6f311d', h3: '#954727',
  v2: '#8f1c28', b0: '#5b3d13', b1: '#8e6522', b2: '#c59b3c', b3: '#eed47c',
};
/** At night the lift is a little darker, as the world is, but its lamp still glows. */
const NIGHT = [0.72, 0.72, 0.84];
const nightCols = new Map<string, string>();
function col(c: string, night: boolean): string {
  const hex = PAL[c] ?? c;
  if (!night || c === 'lw') return hex;
  let v = nightCols.get(hex);
  if (!v) { v = nightHex(hex); nightCols.set(hex, v); }
  return v;
}
function nightHex(hex: string): string {
  return `#${(hex.match(/\w\w/g) ?? []).map((h, i) => Math.round(parseInt(h, 16) * NIGHT[i]).toString(16).padStart(2, '0')).join('')}`;
}

type G = CanvasRenderingContext2D;
const rect = (g: G, x: number, y: number, w: number, h: number, c: string, night: boolean) => {
  g.fillStyle = col(c, night);
  g.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

function canvas(w: number, h: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = Math.max(1, w);
  c.height = Math.max(1, h);
  return c;
}

let fontsReady = false;
let fontsAsked = false;
function wantFonts(): void {
  if (fontsAsked || typeof document === 'undefined' || !document.fonts?.load) return;
  fontsAsked = true;
  void Promise.all([document.fonts.load('16px m6x11')]).then(() => { fontsReady = true; cache.clear(); }, () => {});
}

/** Width of a pixel-face string in art pixels. */
function measure(text: string, size: number, face: string): number {
  const c = canvas(1, 1);
  const g = c?.getContext('2d');
  if (!g) return text.length * (size / 2.6);
  g.font = `${size}px ${face}`;
  return Math.ceil(g.measureText(text).width);
}

/** Pixel text: drawn, then its alpha thresholded, so the face stays crisp when scaled up. */
function text(g: G, s: string, x: number, y: number, c: string, night: boolean, size = 16, face = 'm6x11', align: 'left' | 'center' = 'left'): void {
  const t = canvas(1, 1);
  const tg = t?.getContext('2d');
  if (!t || !tg) return;
  tg.font = `${size}px ${face}`;
  const w = Math.ceil(tg.measureText(s).width) + 4;
  t.width = w; t.height = size * 2;
  tg.font = `${size}px ${face}`; tg.fillStyle = '#000'; tg.textBaseline = 'top'; tg.fillText(s, 2, 2);
  const d = tg.getImageData(0, 0, t.width, t.height);
  const px = d.data;
  const rgb = (col(c, night).match(/\w\w/g) ?? []).map((h) => parseInt(h, 16));
  for (let i = 0; i < px.length; i += 4) {
    const on = px[i + 3] > 110;
    px[i] = rgb[0]; px[i + 1] = rgb[1]; px[i + 2] = rgb[2]; px[i + 3] = on ? 255 : 0;
  }
  tg.putImageData(d, 0, 0);
  const ox = align === 'center' ? -w / 2 : 0;
  g.drawImage(t, Math.round(x + ox - 2), Math.round(y - 2));
}

/** One art pixel in device pixels: the short side of the screen is about 240 art pixels. */
export function liftScale(vp: Viewport): number {
  return Math.max(1, Math.floor(Math.min(vp.w * vp.dpr, vp.h * vp.dpr) / 240));
}

interface Layout { W: number; H: number; narrow: boolean; lintelH: number; cx: number; dialY: number }
function layout(W: number, H: number): Layout {
  // A narrow screen stacks the floor's name over the dial, so the lintel is a row taller.
  const narrow = W < 400;
  const lintelH = narrow ? 80 : 62;
  return { W, H, narrow, lintelH, cx: Math.floor(W / 2), dialY: lintelH - 6 };
}

/** The doors, shut, with the plate across the seam (split by the caller as they part). */
function drawDoors(L: Layout, name: string, night: boolean): HTMLCanvasElement | null {
  const c = canvas(L.W, L.H);
  const d = c?.getContext('2d');
  if (!c || !d) return null;
  const { W, H, cx, lintelH: top } = L;
  const half = cx;
  rect(d, 0, 0, W, H, 'b1', night);
  const bottom = H - 10;
  for (const [x0, w] of [[0, half], [half, W - half]] as const) {
    const px = x0 + 10;
    const pw = w - 20;
    rect(d, px, top, pw, bottom - top, 'b2', night);
    rect(d, px, top, pw, 1, 'b3', night); rect(d, px, top, 1, bottom - top, 'b3', night);
    rect(d, px + pw - 1, top, 1, bottom - top, 'b0', night); rect(d, px, bottom - 1, pw, 1, 'b0', night);
    for (let x = px + 8; x < px + pw - 6; x += 10) rect(d, x, top + 8, 2, bottom - top - 16, 'b1', night);
    const rail = top + Math.round((bottom - top) * 0.4);
    rect(d, px + 6, rail, pw - 12, 4, 'b0', night); rect(d, px + 6, rail, pw - 12, 1, 'b3', night);
  }
  // the sunburst where the doors meet (a very short screen has no room for it under the plate)
  if (bottom - top > 150) {
    const sy = bottom - 24;
    const sr = Math.min(70, Math.floor(half * 0.6), bottom - top - 110);
    d.strokeStyle = col('b3', night); d.lineWidth = 2;
    for (let k = 0; k < 9; k += 1) {
      const ang = Math.PI * (k / 8);
      d.beginPath(); d.moveTo(cx, sy); d.lineTo(cx + Math.cos(ang) * sr, sy - Math.sin(ang) * sr); d.stroke();
    }
    halfDisc(d, cx, sy, Math.round(sr / 4), 'b1', night);
  }
  rect(d, cx - 2, top, 4, H - top, 'ol', night);
  // the plate: the big face when the name fits, the small one when it does not
  let size = 32;
  let tw = measure(name, size, 'm6x11');
  if (tw + 40 > W - 24) { size = 16; tw = measure(name, size, 'm6x11'); }
  const ph = size === 32 ? 40 : 26;
  const pw = Math.min(W - 16, tw + 40);
  const py = top + Math.min(Math.max(24, Math.round((bottom - top) * 0.14)), Math.max(4, bottom - top - ph - 4));
  const px = cx - Math.round(pw / 2);
  rect(d, px, py, pw, ph, 'ol', night); rect(d, px + 2, py + 2, pw - 4, ph - 4, 'b3', night); rect(d, px + 4, py + 4, pw - 8, ph - 8, 'b2', night);
  text(d, name, cx, py + (size === 32 ? 6 : 5), 'h0', night, size, 'm6x11', 'center');
  rect(d, 0, 0, 2, H, 'ol', night); rect(d, W - 2, 0, 2, H, 'ol', night);
  return c;
}

/** The upper half of a disc in whole pixel rows (no anti-aliased rim). */
function halfDisc(g: G, cx: number, cy: number, r: number, c: string, night: boolean): void {
  for (let y = 0; y < r; y += 1) {
    const hw = Math.round(Math.sqrt(r * r - (y + 0.5) * (y + 0.5)));
    rect(g, cx - hw, cy - y - 1, hw * 2, 1, c, night);
  }
}

const dialAngle = (pos: number) => Math.PI * (0.88 - pos * 0.19);
const R_DIAL = 46;

/** The lintel without its moving parts (needle, arrow, lamp), unlit or lit. */
function drawLintel(L: Layout, to: Level, lit: boolean, night: boolean): HTMLCanvasElement | null {
  const c = canvas(L.W, L.lintelH);
  const g = c?.getContext('2d');
  if (!c || !g) return null;
  const { W, cx, dialY, lintelH: h } = L;
  rect(g, 0, 0, W, h, 'h1', night); rect(g, 0, h - 2, W, 2, 'ol', night); rect(g, 0, 0, W, 2, 'h2', night);
  for (let x = 6; x < W; x += 16) rect(g, x, 8, 8, 2, 'h2', night);
  halfDisc(g, cx, dialY, R_DIAL + 3, 'ol', night);
  halfDisc(g, cx, dialY, R_DIAL, 'p1', night);
  DIAL.forEach((n, i) => {
    const an = dialAngle(i);
    text(g, n, cx + Math.cos(an) * (R_DIAL - 10), dialY - Math.sin(an) * (R_DIAL - 10) - 7, lit && n === to ? 'v2' : 'r1', night, 16, 'm6x11', 'center');
  });
  const floorName = `${LEVEL_WORD[to].toUpperCase()} FLOOR`;
  if (L.narrow) text(g, floorName, cx, 4, lit ? 'b3' : 'h3', night, 16, 'm6x11', 'center');
  else text(g, floorName, cx + R_DIAL + 42, 24, lit ? 'b3' : 'h3', night, 16, 'm6x11');
  return c;
}

/** A 2 px pixel line (the needle), no anti-aliasing. */
function line(g: G, x0: number, y0: number, x1: number, y1: number, c: string, night: boolean): void {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
  for (let i = 0; i <= n; i += 1) {
    const t = n ? i / n : 0;
    rect(g, Math.round(x0 + (x1 - x0) * t) - 1, Math.round(y0 + (y1 - y0) * t) - 1, 2, 2, c, night);
  }
}

const cache = new Map<string, HTMLCanvasElement | null>();
function cached(key: string, make: () => HTMLCanvasElement | null): HTMLCanvasElement | null {
  if (!cache.has(key)) {
    if (cache.size > 16) cache.clear();
    cache.set(key, make());
  }
  return cache.get(key) ?? null;
}

let art: HTMLCanvasElement | null = null;

function artSize(vp: Viewport): { k: number; W: number; H: number } {
  const k = liftScale(vp);
  return { k, W: Math.ceil(Math.round(vp.w * vp.dpr) / k), H: Math.ceil(Math.round(vp.h * vp.dpr) / k) };
}

function parts(trip: LiftTrip, W: number, H: number, lamp: boolean, night: boolean) {
  const L = layout(W, H);
  return {
    doors: cached(`d:${W}x${H}:${trip.name}:${night}:${fontsReady}`, () => drawDoors(L, trip.name, night)),
    lintel: cached(`l:${W}x${H}:${trip.to}:${lamp}:${night}:${fontsReady}`, () => drawLintel(L, trip.to, lamp, night)),
  };
}

/** Build the doors and both lintels before the trip starts, so no frame of it pays for drawing text. */
export function prepareLift(trip: LiftTrip, vp: Viewport, night: boolean): void {
  wantFonts();
  const { W, H } = artSize(vp);
  parts(trip, W, H, false, night);
  parts(trip, W, H, true, night);
}

/** Draw the lift at `ms` into the world canvas (device pixels, identity transform). Nothing when the doors are open. */
export function drawLift(ctx: CanvasRenderingContext2D, trip: LiftTrip, ms: number, vp: Viewport, night: boolean): void {
  wantFonts();
  const tl = liftTimeline(trip);
  if (ms >= tl.total) return;
  const open = trip.reduced ? 0 : doorsOpen(tl, ms);
  const lintelOut = ms < tl.swap ? 1 - steps(ease(seg(ms, tl.lintelIn)), 6) : steps(ease(seg(ms, tl.lintelOut)), 6);
  if (open >= 1 && lintelOut >= 1) return;
  const { k, W, H } = artSize(vp);
  const L = layout(W, H);
  const lamp = trip.reduced || ms >= tl.ding;
  const { doors, lintel } = parts(trip, W, H, lamp, night);
  if (!doors || !lintel) return;
  if (!art || art.width !== W || art.height !== H) art = canvas(W, H);
  const a = art?.getContext('2d');
  if (!art || !a) return;
  a.imageSmoothingEnabled = false;
  a.clearRect(0, 0, W, H);
  // the car shakes a pixel while it travels
  const travelling = !!tl.travel && ms > tl.travel[0] && ms < tl.travel[1];
  const shake = travelling && Math.floor(ms / 50) % 2 ? 1 : 0;
  const half = L.cx;
  const off = Math.round(half * open);
  if (open < 1) {
    a.drawImage(doors, 0, 0, half, H, -off, shake, half, H);
    a.drawImage(doors, half, 0, W - half, H, half + off, shake, W - half, H);
  }
  const ly = -Math.round(L.lintelH * lintelOut);
  a.drawImage(lintel, 0, ly);
  // the needle: from the floor you left to the floor you are going to
  const from = trip.fromPos ?? DIAL_POS(trip.from);
  const to = DIAL_POS(trip.to);
  const an = dialAngle(needleAt(trip, ms));
  const cy = L.dialY + ly;
  line(a, L.cx, cy, L.cx + Math.cos(an) * (R_DIAL - 18), cy - Math.sin(an) * (R_DIAL - 18), 'v2', night);
  rect(a, L.cx - 3, cy - 3, 6, 4, 'ol', night);
  // the direction arrow, lit while the car moves, and the arrival lamp
  const ax = L.cx - R_DIAL - 30;
  const ay = ly + (L.narrow ? 40 : 22);
  const up = to >= from;
  a.fillStyle = col(travelling ? 'lw' : 'h2', night);
  a.beginPath();
  if (up) { a.moveTo(ax - 10, ay + 14); a.lineTo(ax, ay); a.lineTo(ax + 10, ay + 14); }
  else { a.moveTo(ax - 10, ay); a.lineTo(ax, ay + 14); a.lineTo(ax + 10, ay); }
  a.fill();
  const lx = L.cx + R_DIAL + 16;
  rect(a, lx, ay, 14, 14, 'ol', night); rect(a, lx + 2, ay + 2, 10, 10, lamp ? 'lw' : 'h2', night);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(art, 0, 0, W, H, 0, 0, W * k, H * k);
  ctx.restore();
}
